// src/pages/Admin/Adherents/EditAdherentAdminModal.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  FaSave,
  FaTimes,
  FaCloudUploadAlt,
  FaLink,
  FaImage,
  FaCrop,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateAdherent } from "../../../services/adherentServices";
import { getSettings } from "../../../services/settingsServices";
import { toast } from "react-toastify";
import { useUser } from "./../../Home/UserContext";
import {
  uploadAdherentPhoto,
  toAbsolutePhotoUrl,
} from "../../../services/adherentPhotoServices";
import CropPhotoModal from "./../../../components/CropPhotoModal/CropPhotoModal";
import { cropAdherentPhoto } from "../../../services/adherentPhotoServices";
import { toMasterUrl } from "./../../../components/CropPhotoModal/CropPhotoModal";

const EditAdherentAdminModal = ({ isOpen, onClose, refreshList, adherent }) => {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    email_secondaire: "",
    tel: "",
    tel_secondaire: "",
    representant_legal: "",
    adresse: "",
    cp: "",
    commune: "",
    genre: "",
    statut_inscription: "",
    date_naissance: "",
    lieu_naissance: "",
    photo_url: "",
    licence_number: "",
    poids: "",
    saison_active: "",
    notes_adherent: "",
    autorisation_photo: false,
    passsport_number: "",
    createdAt: "",
    taille_tshirt: "",
    nationalite_fra: true,
  });

  const { user } = useUser();
  const roleId = user?.role_id;
  const canEditClubFields = roleId === 1 || roleId === 3; // admin ou enseignant

  const [seasons, setSeasons] = useState([]);
  const [imgOk, setImgOk] = useState(true);

  // Gestion upload
  const [uploadMode, setUploadMode] = useState("url"); // 'url' | 'upload'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // 'webp' | 'jpg' | 'png'
  const fileInputRef = useRef(null);
  const [imgVersion, setImgVersion] = useState(0); // anti-cache preview

  const [isCropOpen, setIsCropOpen] = useState(false);

  // Alerte si la date de naissance initiale est invalide
  const [invalidBirthDate, setInvalidBirthDate] = useState(false);

  // Normalise une valeur MySQL/ISO vers un input date ou signale une invalidité
  const sanitizeDateForInput = (raw) => {
    if (!raw) return { value: "", invalid: false };
    const s = String(raw).trim();
    if (s === "0000-00-00") return { value: "", invalid: true };
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return { value: "", invalid: true };
    // On force AAAA-MM-JJ pour l’input date
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return { value: `${yyyy}-${mm}-${dd}`, invalid: false };
  };

  // Valide un AAAA-MM-JJ saisi dans un <input type="date">
  const isValidDateInput = (s) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(s);
    if (Number.isNaN(dt.getTime())) return false;
    return (
      dt.getFullYear() === y && dt.getMonth() + 1 === m && dt.getDate() === d
    );
  };

  const getPreviousSeason = (currentSeason) => {
    const [startYear, endYear] = (currentSeason || "").split("-").map(Number);
    if (!startYear || !endYear) return "";
    return `${startYear - 1}-${endYear - 1}`;
  };
  const getNextSeason = (currentSeason) => {
    const [startYear, endYear] = (currentSeason || "").split("-").map(Number);
    if (!startYear || !endYear) return "";
    return `${startYear + 1}-${endYear + 1}`;
  };

  const loadSeasons = useCallback(async () => {
    try {
      const settingsData = await getSettings();
      if (settingsData?.length) {
        const active = settingsData[0].saison_active;
        const prev = getPreviousSeason(active);
        const next = getNextSeason(active);
        setSeasons([prev, active, next].filter(Boolean));
      }
    } catch (e) {
      console.error("Erreur chargement settings:", e);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadSeasons();
  }, [isOpen, loadSeasons]);

  useEffect(() => {
    if (!adherent) return;

    const dn = sanitizeDateForInput(adherent.date_naissance);
    const ca = sanitizeDateForInput(adherent.createdAt);

    setInvalidBirthDate(dn.invalid);

    setFormData({
      nom: adherent.nom || "",
      prenom: adherent.prenom || "",
      email: adherent.email || "",
      email_secondaire: adherent.email_secondaire || "",
      tel: adherent.tel || "",
      tel_secondaire: adherent.tel_secondaire || "",
      representant_legal: adherent.representant_legal || "",
      adresse: adherent.adresse || "",
      cp: adherent.cp || "",
      commune: adherent.commune || "",
      genre: adherent.genre || "",
      statut_inscription: adherent.statut_inscription || "",
      date_naissance: dn.value, // <- value vide si invalide
      lieu_naissance: adherent.lieu_naissance || "",
      photo_url: adherent.photo_url || "",
      licence_number: adherent.licence_number || "",
      poids: adherent.poids || "",
      saison_active: adherent.saison_active || "",
      notes_adherent: adherent.notes_adherent || "",
      autorisation_photo: !!adherent.autorisation_photo,
      passsport_number: adherent.passsport_number || "",
      createdAt: ca.value, // sécurisé aussi, par prudence
      taille_tshirt: adherent.taille_tshirt || "",
      nationalite_fra: !!adherent.nationalite_fra,
    });

    setImgOk(true);
    setUploadMode("url");
    setUploadProgress(0);
    setIsUploading(false);
    setImgVersion((v) => v + 1);
  }, [adherent]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "date_naissance") {
      // Efface l’alerte si vide ou valide, sinon l’affiche
      if (!value) {
        setInvalidBirthDate(false);
      } else {
        setInvalidBirthDate(!isValidDateInput(value));
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openFilePicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    if (!adherent?.id) {
      toast.error("ID adhérent manquant.");
      return;
    }
    try {
      setIsUploading(true);
      setUploadProgress(0);
      const res = await uploadAdherentPhoto(adherent.id, file, {
        format: outputFormat,
        onUploadProgress: (pe) => {
          if (!pe.total) return;
          const percent = Math.round((pe.loaded * 100) / pe.total);
          setUploadProgress(percent);
        },
      });
      setFormData((prev) => ({ ...prev, photo_url: res.photo_url || "" }));
      setImgOk(true);
      setImgVersion((v) => v + 1); // force refresh preview
      toast.success("Photo uploadée et traitée (400×400).");
    } catch (err) {
      console.error(err);
      toast.error(`Erreur upload: ${err?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (invalidBirthDate && formData.date_naissance) {
      toast.error(
        "La date de naissance est invalide. Corrige-la ou laisse le champ vide."
      );
      return;
    }
    try {
      if (isUploading) {
        toast.info("Patiente, l'upload est en cours…");
        return;
      }
      const payload = { ...formData };

      if (!canEditClubFields) {
        delete payload.statut_inscription;
        delete payload.saison_active;
      }

      await updateAdherent(adherent.id, payload);
      refreshList?.();
      onClose?.();
      toast.success("Adhérent mis à jour avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'adhérent", error);
      toast.error("Erreur lors de la mise à jour de l'adhérent");
    }
  };

  if (!isOpen) return null;

  const absolutePhoto = toAbsolutePhotoUrl(formData.photo_url);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative w-full max-w-4xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Modifier un adhérent
          </h3>
          <p className="text-white/90 text-center text-xs mt-1">
            Mettez à jour les informations de l'adhérent ici.
          </p>
        </div>

        {/* Contenu scrollable */}
        <form onSubmit={handleSubmit}>
          <div className="p-5 sm:p-6 space-y-6 max-h-[75vh] overflow-y-auto">
            {/* ÉTAT CIVIL */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
              <div className="px-4 py-3 border-b border-slate-200/70 dark:border-slate-800/70">
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  État civil
                </h4>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Nom & Prénom + infos */}
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Nom
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Prénom
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Genre
                    </label>
                    <select
                      name="genre"
                      value={formData.genre}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    >
                      <option value="">— Sélectionner —</option>
                      <option value="Masculin">Masculin</option>
                      <option value="Féminin">Féminin</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Date de naissance
                    </label>
                    <input
                      type="date"
                      name="date_naissance"
                      value={formData.date_naissance}
                      onChange={handleChange}
                      placeholder="AAAA-MM-JJ"
                      aria-invalid={invalidBirthDate ? "true" : "false"}
                      aria-describedby={
                        invalidBirthDate ? "date-naissance-help" : undefined
                      }
                      className={
                        "w-full rounded-xl border bg-white dark:bg-slate-900 px-3 py-2 " +
                        (invalidBirthDate
                          ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500 "
                          : "border-slate-300 dark:border-slate-700 ")
                      }
                    />
                    {invalidBirthDate && (
                      <p
                        id="date-naissance-help"
                        className="mt-1 text-xs text-rose-600"
                      >
                        Date invalide à modifier.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Lieu de naissance
                    </label>
                    <input
                      type="text"
                      name="lieu_naissance"
                      value={formData.lieu_naissance}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Numéro de licence
                    </label>
                    <input
                      type="text"
                      name="licence_number"
                      value={formData.licence_number}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Poids (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      name="poids"
                      value={formData.poids}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Taille de T-shirt
                    </label>
                    <select
                      name="taille_tshirt"
                      value={formData.taille_tshirt}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    >
                      <option value="">— Sélectionner —</option>
                      <option value="XS">8-12 ans</option>
                      <option value="XS">XS</option>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                      <option value="XL">XL</option>
                      <option value="XXL">XXL</option>
                      <option value="XXXL">XXXL</option>
                    </select>
                  </div>
                </div>

                {/* Photo : URL / Upload + preview */}
                <div
                  className="md:col-span-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="block text-sm font-medium mb-2">
                    Photo adhérent
                  </label>

                  <div className="flex items-center gap-4 mb-2">
                    <label
                      className="inline-flex items-center gap-2 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="radio"
                        name="photoMode"
                        value="url"
                        checked={uploadMode === "url"}
                        onChange={() => setUploadMode("url")}
                      />
                      <span className="flex items-center gap-2 text-sm">
                        <FaLink /> Lien URL
                      </span>
                    </label>
                    <label
                      className="inline-flex items-center gap-2 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="radio"
                        name="photoMode"
                        value="upload"
                        checked={uploadMode === "upload"}
                        onChange={() => setUploadMode("upload")}
                      />
                      <span className="flex items-center gap-2 text-sm">
                        <FaCloudUploadAlt /> Uploader
                      </span>
                    </label>
                  </div>

                  {uploadMode === "url" ? (
                    <input
                      type="text"
                      name="photo_url"
                      value={formData.photo_url}
                      onChange={(e) => {
                        setImgOk(true);
                        handleChange(e);
                        setImgVersion((v) => v + 1);
                      }}
                      placeholder="https://… ou /uploads/…"
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div
                      className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white/70 dark:bg-slate-900/30"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {/* INPUT FILE CACHÉ */}
                      <input
                        ref={fileInputRef}
                        id="adherent-photo-file"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />

                      <div className="grid grid-cols-1 gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className=" bg-primary rounded-xl gap-2 px-3 py-1.5 text-sm text-white hover:bg-primary-dark flex items-center hover:bg-white/90 hover:text-gray-800 transition ring-1 ring-primary/70 hover:ring-primary"
                            onClick={openFilePicker}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            Choisir un fichier
                          </button>

                          <div className="flex items-center gap-2">
                            <span className="text-sm">Format</span>
                            <select
                              className="select select-bordered select-sm"
                              value={outputFormat}
                              onChange={(e) => setOutputFormat(e.target.value)}
                              disabled={isUploading}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="webp">WEBP (recommandé)</option>
                              <option value="jpg">JPG</option>
                              <option value="png">PNG</option>
                            </select>
                          </div>
                        </div>

                        {isUploading && (
                          <div>
                            <progress
                              className="progress w-full"
                              value={uploadProgress}
                              max="100"
                            ></progress>
                            <div className="text-xs mt-1">
                              {uploadProgress}%
                            </div>
                          </div>
                        )}

                        <p className="text-xs opacity-70">
                          L’image sera traitée côté serveur en carré 400×400 et
                          optimisée.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Aperçu */}
                  <div className="mt-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <FaImage />
                      <span>
                        Aperçu
                        {uploadMode === "upload"
                          ? " (cliquer pour choisir un fichier)"
                          : ""}
                      </span>
                    </div>

                    <div
                      className="mt-2 h-28 w-28 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (uploadMode === "upload") openFilePicker(e);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {formData.photo_url ? (
                        <img
                          src={`${absolutePhoto}?v=${imgVersion}`}
                          alt="Aperçu"
                          onError={() => setImgOk(false)}
                          className="h-full w-full object-cover pointer-events-none"
                        />
                      ) : (
                        <span className="text-xs text-slate-500 px-2 text-center">
                          Aucun visuel
                        </span>
                      )}
                    </div>

                    {/* Bouton recadrage (visible si une photo existe déjà) */}
                    {formData.photo_url && (
                      <button
                        type="button"
                        className="mt-2 h-12 w-28 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCropOpen(true);
                        }}
                      >
                        Recadrer / Zoom
                      </button>
                    )}
                    <p className="text-xs text-slate-500">
                      Le recadrage de la photo est possible de suite après
                      l'upload d'un fichier.
                    </p>

                    {!imgOk && (
                      <div className="mt-2 text-xs text-rose-600">
                        Impossible de charger l’image.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* COORDONNÉES */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
              <div className="px-4 py-3 border-b border-slate-200/70 dark:border-slate-800/70">
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Coordonnées
                </h4>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email secondaire
                  </label>
                  <input
                    type="email"
                    name="email_secondaire"
                    value={formData.email_secondaire}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Téléphone
                  </label>
                  <input
                    type="text"
                    name="tel"
                    value={formData.tel}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Téléphone secondaire
                  </label>
                  <input
                    type="text"
                    name="tel_secondaire"
                    value={formData.tel_secondaire}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Représentant légal
                  </label>
                  <input
                    type="text"
                    name="representant_legal"
                    value={formData.representant_legal}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Adresse
                  </label>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Code postal
                  </label>
                  <input
                    type="text"
                    name="cp"
                    value={formData.cp}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Commune
                  </label>
                  <input
                    type="text"
                    name="commune"
                    value={formData.commune}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
              </div>
            </div>

            {/* INFOS CLUB */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
              <div className="px-4 py-3 border-b border-slate-200/70 dark:border-slate-800/70">
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Infos club
                </h4>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Statut d'inscription
                  </label>
                  {canEditClubFields ? (
                    <select
                      name="statut_inscription"
                      value={formData.statut_inscription}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    >
                      <option value="">— Sélectionner —</option>
                      <option value="Licencié">Licencié</option>
                      <option value="Inscrit">Inscrit</option>
                      <option value="Cours d'essai">Cours d'essai</option>
                      <option value="Licencié - Arrêt">Licencié - Arrêt</option>
                      <option value="Non Licencié - Arrêt">
                        Non Licencié - Arrêt
                      </option>
                      <option value="Licencié Hors Club">
                        Licencié Hors Club
                      </option>
                      <option value="Ancien Licencié">Ancien Licencié</option>
                      <option value="Ancien Licencié et Arrêt">
                        Ancien Licencié et Arrêt
                      </option>
                    </select>
                  ) : (
                    <div className="w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-slate-700 dark:text-slate-200">
                      {formData.statut_inscription || "—"}
                      <div className="text-[11px] text-slate-500 mt-1">
                        Champ géré par le club.
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Saison active
                  </label>
                  {canEditClubFields ? (
                    <select
                      name="saison_active"
                      value={formData.saison_active}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    >
                      {seasons.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-slate-700 dark:text-slate-200">
                      {formData.saison_active || "—"}
                      <div className="text-[11px] text-slate-500 mt-1">
                        Champ géré par le club.
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    N° Pass’Sport (facultatif)
                  </label>
                  <input
                    type="text"
                    name="passsport_number"
                    value={formData.passsport_number}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Date de création (info)
                  </label>
                  <input
                    type="date"
                    name="createdAt"
                    value={formData.createdAt}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes_adherent"
                    value={formData.notes_adherent}
                    onChange={handleChange}
                    rows={4}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="autorisation_photo"
                      checked={formData.autorisation_photo}
                      onChange={handleChange}
                      className="h-5 w-5"
                    />
                    <span className="text-sm">Autorisation photo</span>
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="nationalite_fra"
                      checked={formData.nationalite_fra}
                      onChange={handleChange}
                      className="h-5 w-5"
                    />
                    <span className="text-sm">Nationalité française</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div
            className="px-5 pb-5 sm:px-6 sm:pb-6 flex justify-end gap-2"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text="Enregistrer"
              icon={FaSave}
              type="submit"
              className="!rounded-xl gap-2"
              disabled={isUploading}
            />
          </div>
        </form>
      </div>
      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={adherent?.id}
        imageUrl={`${toAbsolutePhotoUrl(
          toMasterUrl(formData.photo_url)
        )}?v=${imgVersion}`}
        onCropped={(newUrl) => {
          // le backend renvoie un chemin /uploads/...
          setFormData((p) => ({ ...p, photo_url: newUrl }));
          setImgVersion((v) => v + 1); // anti-cache
        }}
        format="webp"
        cropAction={cropAdherentPhoto}
      />
    </div>
  );
};

export default EditAdherentAdminModal;
